import { UTCDate } from "@date-fns/utc";
import { format, getWeek, parse, startOfWeek } from "date-fns";
import { TFunction, t } from "i18next";
import { AggregationType } from "./fields";
import { toastError } from "@utils/toasts";
import { useUserStore } from "src/store";
import { DEFAULT_PERIOD_FORMAT } from "@utils/constants";

export type PeriodType =
  0    // Day
  | 1    // Week
  | 2    // Month
  | 3    // Quarter
  | 4;   // Year

const PeriodTypeDay = 0;
const PeriodTypeWeek = 1;
const PeriodTypeMonth = 2;
const PeriodTypeQuarter = 3;
const PeriodTypeYear = 4;

export const PeriodTypeValues: PeriodType[] = [PeriodTypeDay, PeriodTypeWeek, PeriodTypeMonth, PeriodTypeQuarter, PeriodTypeYear];

export type PeriodMonth = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
export const PeriodMonthJanuary = 1;
export const PeriodMonthFebruary = 2;
export const PeriodMonthMarch = 3;
export const PeriodMonthApril = 4;
export const PeriodMonthMay = 5;
export const PeriodMonthJune = 6;
export const PeriodMonthJuly = 7;
export const PeriodMonthAugust = 8;
export const PeriodMonthSeptember = 9;
export const PeriodMonthOctober = 10;
export const PeriodMonthNovember = 11;
export const PeriodMonthDecember = 12;
export const PeriodMonthValues: PeriodMonth[] = [PeriodMonthJanuary, PeriodMonthFebruary, PeriodMonthMarch, PeriodMonthApril, PeriodMonthMay, PeriodMonthJune, PeriodMonthJuly, PeriodMonthAugust, PeriodMonthSeptember, PeriodMonthOctober, PeriodMonthNovember, PeriodMonthDecember];

export type PeriodQuarter = 1 | 2 | 3 | 4;
export const PeriodQuarter1 = 1;
export const PeriodQuarter2 = 2;
export const PeriodQuarter3 = 3;
export const PeriodQuarter4 = 4;
export const PeriodQuarterValues: PeriodQuarter[] = [PeriodQuarter1, PeriodQuarter2, PeriodQuarter3, PeriodQuarter4];

export type PeriodYear = number;
export const PeriodYearValues: PeriodYear[] = []
const PeriodYearMin = 1900;
const PeriodYearMax = 2100;
for (let year = PeriodYearMin; year <= PeriodYearMax; year++) {
  PeriodYearValues.push(year);
}

const DATE_FORMAT = 'yyyy-MM-dd'
export const DEFAULT_PERIOD_TYPE = PeriodTypeWeek;

export function getPeriodTypeLabel(periodType: PeriodType, t: TFunction): string {
  return t(`field.period.type.${periodType}`);
}

export function getPeriodMonthLabel(periodMonth: PeriodMonth, t: TFunction, short: boolean = false): string {
  if (short) {
    return t(`field.period.month.short.${periodMonth}`);
  }
  return t(`field.period.month.${periodMonth}`);
}

export function getPeriodQuarterLabel(periodQuarter: PeriodQuarter, t: TFunction, long: boolean = false): string {
  if (long) {
    return t(`field.period.quarter.long.${periodQuarter}`)+ " ";
  }
  return t(`field.period.quarter.${periodQuarter}`);
}

export function getPeriodWeekValue(week: number, year: number, long: boolean = false, displayYear: boolean = true): string {
  if (long) {
    return t(`field.period.week.long.prefix`) + week + (displayYear ? " " + year : "");
  }
  return t(`field.period.week.prefix`) + week + (displayYear ? " " + year : "");
}

export function getPeriodWeekLabel(period: Period, t: TFunction, long: boolean = false, displayYear: boolean = true): string {
  let yearValue = period.year();
  const monthValue = period.month();
  const weekValue = period.week();
  if (weekValue && yearValue && monthValue && weekValue === 1 && monthValue === 12) {
      yearValue = yearValue + 1;
  }
  return getPeriodWeekValue(weekValue, yearValue, long, displayYear);
}

export function getLastWeekOfYear(year: number): number {
  let lastWeek = getWeek(new UTCDate(year, 11, 31), { weekStartsOn: 1 });
  if (lastWeek === 1) {
    let endOfYear = new UTCDate(year, 11, 31);
    endOfYear.setDate(endOfYear.getDate() - 7);
    lastWeek = getWeek(endOfYear, { weekStartsOn: 1 });
  }
  return lastWeek;
}

export function getCurrentYear(): number {
  return new UTCDate().getFullYear();
}

export function getCurrentMonth(): PeriodMonth {
  return new UTCDate().getMonth() + 1 as PeriodMonth;
}

export function getCurrentQuarter(): PeriodQuarter {
  return Math.floor((getCurrentMonth() - 1) / 3) + 1 as PeriodQuarter;
}

export function getCurrentWeek(): number {
  return getWeek(new UTCDate(), { weekStartsOn: 1 });
}

export function getStartOfWeek(year: number, week: number): Date {
  const date = parse(`${week} ${year}`, "I R", new UTCDate());
  return startOfWeek(date, { weekStartsOn: 1 });
}

export function normalizePeriod(period: Period, periodType: PeriodType | null = null): Period {

  let pType = periodType || period.type;
  const [start] = period.limitsOf(pType);
  const startStr = format(start, DATE_FORMAT);

  if (period.isCustom()) {
    const endDate = new UTCDate(period.endDate!);
    const endPeriod = Period.fromDate(endDate, pType);
    const [endStart] = endPeriod.limitsOf(pType);
    const endStartStr = format(endStart, DATE_FORMAT);
    return Period.create(startStr, pType, endStartStr);
  }

  return Period.create(startStr, pType);
}

export function upperPeriodType(periodType: PeriodType): PeriodType {
  if (periodType === PeriodTypeMonth) {
    return PeriodTypeYear;
  }
  return Math.min(periodType + 1, PeriodTypeYear) as PeriodType;
}

export function lowerPeriodType(periodType: PeriodType): PeriodType {
  return Math.max(periodType - 1, PeriodTypeDay) as PeriodType;
}

class Period {
  constructor(
    public date: string,
    public type: PeriodType,
    private parsedDate?: Date,
    public endDate?: string,
    public parsedEndDate?: Date
  ) { }

  static copyOf(period: Period): Period {  
    return new Period(period.date, period.type, period.parsedDate, period.endDate, period.parsedEndDate);
  }

  static fromString(periodStr: string): Period {
    const period: Period = JSON.parse(periodStr);
    return Period.create(period.date, period.type, period.endDate);
  }

  static createRange(date: string, periodType: PeriodType, endDate: string): Period {
    return Period.create(date, periodType, endDate);
  }

  static newPeriod(year: number, month: PeriodMonth, day: number, periodType: PeriodType = PeriodTypeDay): Period {
    const newDate = new UTCDate(year, month - 1, day);
    return Period.fromDate(newDate, periodType);
  }

  static fromObject(value: any): Period | undefined {
    if (!value || typeof value !== 'object') return undefined;
    return Period.create(value.date, value.type ?? PeriodTypeDay, value.endDate);
  }

  static isCompatibleToDateLayout(input: any, layout: string): boolean {
    if (typeof input !== 'string') return false
    return !isNaN(Date.parse(input))
  }

  static today(): Period {
    return Period.fromDate(new UTCDate())
  }

  static fromDate(date: Date, periodType: PeriodType = PeriodTypeDay): Period {
    return new Period(
      safeISODateString(date),
      periodType,
      date
    )
  }

  static create(date: string, periodType: PeriodType, endDate?: string): Period {
    const parsedDate = new UTCDate(date)
    if (isNaN(parsedDate.getTime())) {
      throw new Error('Invalid date')
    }
    let parsedEndDate: Date | undefined;
    if (endDate) {
      parsedEndDate = new UTCDate(endDate)
      if (isNaN(parsedEndDate.getTime())) {
        throw new Error('Invalid end date')
      }
    }
    return new Period(date, periodType, parsedDate, endDate, parsedEndDate)
  }

  static monthValue(monthName: string, t: TFunction): PeriodMonth {
    const target = monthName.trim();
    for (const month of PeriodMonthValues) {
      // Match both the long ("Juillet") and short ("Juil.") labels so a period
      // copied in any display format round-trips back to the right month
      // instead of silently falling back to January.
      if (getPeriodMonthLabel(month, t) === target || getPeriodMonthLabel(month, t, true) === target) {
        return month;
      }
    }
    return 1;
  }

  static quarterValue(quarterName: string, t: TFunction): PeriodQuarter {
    const quarterPrefix = t("field.period.quarter.prefix");
    const quarterNumberStr = quarterName.substring(quarterPrefix.length);
    const quarterNumber = parseInt(quarterNumberStr);
    return quarterNumber as PeriodQuarter;
  }

  static weekValue(weekName: string, t: TFunction): number {
    const weekPrefix = t("field.period.week.prefix");
    const weekNumberStr = weekName.substring(weekPrefix.length);
    const weekNumber = parseInt(weekNumberStr);
    return weekNumber;
  }

  isCustom(): boolean {
    return this.endDate !== undefined && this.endDate !== null && this.endDate !== "";
  }

  toToday(): Period {
    const newDate = new UTCDate();
    return new Period(
      safeISODateString(newDate),
      this.type,
      newDate
    )
  }

  toPeriodType(periodType: PeriodType): Period {
    return new Period(this.date, periodType, this.parsedDate, this.endDate, this.parsedEndDate)
  }

  private parseDate(): Date {
    if (this.parsedDate) return this.parsedDate
    const parsed = new UTCDate(this.date)
    if (isNaN(parsed.getTime())) {
      throw new Error('Invalid date')
    }
    return parsed
  }

  private parseEndDate(): Date | undefined {
    if (!this.isCustom()) return undefined
    if (this.parsedEndDate) return this.parsedEndDate
    const parsed = new UTCDate(this.endDate!)
    if (isNaN(parsed.getTime())) {
      throw new Error('Invalid end date')
    }
    return parsed
  }

  day(): number {
    try {
      return this.parseDate().getDate()
    } catch {
      return 0
    }
  }

  endDay(): number {
    if (!this.isCustom()) return -1
    try {
      return this.parseEndDate()?.getDate() || 0
    } catch {
      return 0
    }
  }

  isoDate(): string {
    return this.date;
  }

  week(): number {
    return getWeek(this.date, { weekStartsOn: 1 })
  }

  endWeek(): number {
    if (!this.isCustom()) return -1
    try {
      const endDate = this.parseEndDate()
      return endDate ? getWeek(endDate, { weekStartsOn: 1 }) : 0
    } catch {
      return 0
    }
  }

  month(): number {
    try {
      return this.parseDate().getMonth() + 1
    } catch {
      return 0
    }
  }

  endMonth(): number {
    if (!this.isCustom()) return -1
    try {
      const endDate = this.parseEndDate()
      return endDate ? endDate.getMonth() + 1 : 0
    } catch {
      return 0
    }
  }

  setMonth(month: PeriodMonth): void {
    const newDate = new UTCDate(this.date);
    newDate.setMonth(month - 1);
    this.date = safeISODateString(newDate);
    this.parsedDate = newDate;
  }

  year(): number {
    try {
      return this.parseDate().getFullYear()
    } catch {
      return 0
    }
  }

  endYear(): number {
    if (!this.isCustom()) return -1
    try {
      return this.parseEndDate()?.getFullYear() || 0
    } catch {
      return 0
    }
  }

  setYear(year: number): void {
    const newDate = new UTCDate(this.date);
    newDate.setFullYear(year);
    this.date = safeISODateString(newDate);
    this.parsedDate = newDate;
  }

  quarter(): number {
    try {
      return Math.floor((this.month() - 1) / 3) + 1
    } catch {
      return 0
    }
  }

  endQuarter(): number {
    if (!this.isCustom()) return -1
    try {
      const endMonth = this.endMonth()
      return endMonth > 0 ? Math.floor((endMonth - 1) / 3) + 1 : 0
    } catch {
      return 0
    }
  }

  limits(): [Date, Date] {
    return this.limitsOf()
  }

  limitsOf(pType?: PeriodType): [Date, Date] {
    const date = this.parseDate()
    const periodType = pType || this.type

    if (this.isCustom()) {
      const endDate = this.parseEndDate()!
      const [startDate] = this.dateLimits(date, periodType)
      const [, endDateLimit] = this.dateLimits(endDate, periodType)
      return [startDate, endDateLimit]
    }

    return this.dateLimits(date, periodType)
  }

  private dateLimits(date: Date, periodType: PeriodType): [Date, Date] {
    switch (periodType) {
      case PeriodTypeDay:
        return [date, date]

      case PeriodTypeWeek: {
        const weekDay = date.getDay()
        const weekStart = new UTCDate(date)
        weekStart.setDate(date.getDate() - weekDay + 1)
        const weekEnd = new UTCDate(weekStart)
        weekEnd.setDate(weekStart.getDate() + 6)
        return [weekStart, weekEnd]
      }

      case PeriodTypeMonth: {
        const monthStart = new UTCDate(date.getFullYear(), date.getMonth(), 1)
        const monthEnd = new UTCDate(date.getFullYear(), date.getMonth() + 1, 0)
        return [monthStart, monthEnd]
      }

      case PeriodTypeQuarter: {
        const quarterStart = new UTCDate(date.getFullYear(), Math.floor(date.getMonth() / 3) * 3, 1)
        const quarterEnd = new UTCDate(quarterStart)
        quarterEnd.setMonth(quarterStart.getMonth() + 3)
        quarterEnd.setDate(0)
        return [quarterStart, quarterEnd]
      }

      case PeriodTypeYear: {
        const yearStart = new UTCDate(date.getFullYear(), 0, 1)
        const yearEnd = new UTCDate(date.getFullYear(), 11, 31)
        return [yearStart, yearEnd]
      }

      default:
        throw new Error(`Invalid period type (${periodType})`)
    }
  }

  contains(date: Date): boolean {
    try {
      const [start, end] = this.limits()
      return this.areEqualDates(date, start) || 
             this.areEqualDates(date, end) || 
             (date > start && date < end)
    } catch {
      return false
    }
  }

  isInPeriod(date: Date): boolean {
    return this.isInPeriodOf(date)
  }

  isInPeriodOf(date: Date, periodType?: PeriodType): boolean {
    try {
      const [start, end] = this.limitsOf(periodType)
      return date > start && date < end
    } catch {
      return false
    }
  }

  private areEqualDates(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate()
  }

  isInside(period: Period): boolean {
    if (this.comparePeriodTypes(this.type, period.type) > 0) {
      return false
    }
    try {
      const parsedDate = this.parseDate()
      return period.contains(parsedDate)
    } catch {
      return false
    }
  }

  compare(other: Period): number {
    const minPeriodType = Math.min(this.type, other.type) as PeriodType
    const normP1 = normalizePeriod(this, minPeriodType)
    const normP2 = normalizePeriod(other, minPeriodType)
    return normP1.date.localeCompare(normP2.date)
  }

  private comparePeriodTypes(t1: PeriodType, t2: PeriodType): number {
    return t1 - t2
  }

  add(years: number, months: number, days: number): Period {
    try {
      const normalized = normalizePeriod(this, this.type)
      const date = normalized.parseDate()
      const newDate = new UTCDate(date)
      newDate.setFullYear(date.getFullYear() + years)
      newDate.setMonth(date.getMonth() + months)
      newDate.setDate(date.getDate() + days)

      if (this.isCustom()) {
        const endDate = this.parseEndDate()!
        const newEndDate = new UTCDate(endDate)
        newEndDate.setFullYear(endDate.getFullYear() + years)
        newEndDate.setMonth(endDate.getMonth() + months)
        newEndDate.setDate(endDate.getDate() + days)
        return new Period(
          safeISODateString(newDate),
          this.type,
          newDate,
          safeISODateString(newEndDate),
          newEndDate
        )
      }

      return new Period(
        safeISODateString(newDate),
        this.type,
        newDate
      )
    } catch {
      return this
    }
  }

  next(): Period {
    switch (this.type) {
      case PeriodTypeDay:
        return this.add(0, 0, 1)
      case PeriodTypeWeek:
        return this.add(0, 0, 7)
      case PeriodTypeMonth:
        return this.add(0, 1, 0)
      case PeriodTypeQuarter:
        return this.add(0, 3, 0)
      case PeriodTypeYear:
        return this.add(1, 0, 0)
      default:
        return this
    }
  }

  previous(): Period {
    switch (this.type) {
      case PeriodTypeDay:
        return this.add(0, 0, -1)
      case PeriodTypeWeek:
        return this.add(0, 0, -7)
      case PeriodTypeMonth:
        return this.add(0, -1, 0)
      case PeriodTypeQuarter:
        return this.add(0, -3, 0)
      case PeriodTypeYear:
        return this.add(-1, 0, 0)
      default:
        return this
    }
  }

  range(periodType?: PeriodType): Period[] {
    try {
      const [start, end] = this.limitsOf(periodType)
      const periods: Period[] = []
      const current = new UTCDate(start)
      const endTime = end.getTime()

      while (current.getTime() <= endTime) {
        periods.push(Period.fromDate(new UTCDate(current)))
        current.setDate(current.getDate() + 1)
      }

      return periods
    } catch {
      return []
    }
  }

  toString(t: TFunction, periodFormat: string = DEFAULT_PERIOD_FORMAT, periodType: PeriodType = this.type): string {
    const startStr = this.formatPeriod(t, periodFormat, periodType, false)
    
    if (this.isCustom()) {
      const endStr = this.formatPeriod(t, periodFormat, periodType, true)
      return `${startStr} - ${endStr}`
    }
    
    return startStr
  }

  private formatPeriod(t: TFunction, periodFormat: string, periodType: PeriodType, isEnd: boolean): string {
    const date = isEnd ? this.endDate! : this.date
    const weekValue = isEnd ? this.endWeek() : this.week()
    const monthValue = isEnd ? this.endMonth() : this.month()
    const quarterValue = isEnd ? this.endQuarter() : this.quarter()
    const yearValue = isEnd ? this.endYear() : this.year()

    switch (periodType) {
      case PeriodTypeDay:
        return format(date, periodFormat)
      case PeriodTypeWeek:
        switch (periodFormat) {
          case "WWWW":
            return getPeriodWeekValue(weekValue, yearValue, true, false)
          case "WWW":
            return getPeriodWeekValue(weekValue, yearValue, false, false)
          case "WW":
            return weekValue.toString()
          case "WWWW yyyy":
            return getPeriodWeekValue(weekValue, yearValue, true, true)
          case "WWW yyyy":
            return getPeriodWeekValue(weekValue, yearValue, false, true)
          default:
            return getPeriodWeekValue(weekValue, yearValue, false, true)
        }
      case PeriodTypeMonth:
        switch (periodFormat) {
          case "MMMM":
            return getPeriodMonthLabel(monthValue as PeriodMonth, t, false)
          case "MMM":
            return getPeriodMonthLabel(monthValue as PeriodMonth, t, true)
          case "MM":
            return format(date, "MM")
          case "MMMM yyyy":
            return getPeriodMonthLabel(monthValue as PeriodMonth, t) + " " + yearValue
          case "MMM yyyy":
            return getPeriodMonthLabel(monthValue as PeriodMonth, t, true) + " " + yearValue
          case "MM/yyyy":
            return format(date, "MM/yyyy")
          default:
            return getPeriodMonthLabel(monthValue as PeriodMonth, t, false) + " " + yearValue
        }
      case PeriodTypeQuarter:
        switch (periodFormat) {
          case "QQQQ":
            return getPeriodQuarterLabel(quarterValue as PeriodQuarter, t, true)
          case "QQQ":
            return getPeriodQuarterLabel(quarterValue as PeriodQuarter, t, false)
          case "QQ":
            return quarterValue.toString()
          case "QQQQ yyyy":
            return getPeriodQuarterLabel(quarterValue as PeriodQuarter, t, true) + " " + yearValue
          case "QQQ yyyy":
            return getPeriodQuarterLabel(quarterValue as PeriodQuarter, t, false) + " " + yearValue
          default:
            return getPeriodQuarterLabel(quarterValue as PeriodQuarter, t, false) + " " + yearValue
        }
      case PeriodTypeYear:
        return yearValue.toString()
      default:
        return date
    }
  }

  toNormalizedString(periodType: PeriodType | null = null): string {
    let pType = periodType || this.type;
    const normalizedPeriod = normalizePeriod(this, pType);
    
    if (normalizedPeriod.isCustom()) {
      return pType + "_" + normalizedPeriod.isoDate() + "-" + pType + "_" + normalizedPeriod.endDate;
    }
    
    return pType + "_" + normalizedPeriod.isoDate();
  }
}

export function getAllWeekValues(year: number): number[] {
    const lastWeekOfYear = getLastWeekOfYear(year);
    const res: number[] = [];
    for (let i = 1; i <= lastWeekOfYear; i++) {
        res.push(i);
    }
    return res;
}

export function aggregationToPeriodType(aggregation: AggregationType): PeriodType {
    switch (aggregation) {
        case AggregationType.DAY:
            return PeriodTypeDay;
        case AggregationType.WEEK:
            return PeriodTypeWeek;
        case AggregationType.MONTH:
            return PeriodTypeMonth;
        case AggregationType.QUARTER:
            return PeriodTypeQuarter;
        case AggregationType.YEAR:
            return PeriodTypeYear;
    }
    return PeriodTypeDay;
}

export function safeISODateString(date: Date): string {
  try {
    return date.toISOString().split('T')[0];
  } catch {
    console.error("Invalid date", date);
    const currLang = useUserStore.getState().lang;
    // TODO: use i18n
    if (currLang === "fr-FR") {
      toastError("Date invalide");
    } else {
      toastError("Invalid date");
    }
    return new UTCDate().toISOString().split('T')[0];
  }
}

export function getQuarterPeriod(quarter: PeriodQuarter, year: number): Period {
  const month = (quarter - 1) * 3 + 1;
  return Period.newPeriod(year, month as PeriodMonth, 1, PeriodTypeQuarter);
}

export function getWeekPeriod(week: number, year: number): Period {
  const start = getStartOfWeek(year, week);
  return Period.fromDate(start, PeriodTypeWeek);
}

export function getMonthPeriod(month: PeriodMonth, year: number): Period {
  return Period.newPeriod(year, month, 1, PeriodTypeMonth);
}

export { Period, PeriodTypeDay, PeriodTypeMonth, PeriodTypeQuarter, PeriodTypeWeek, PeriodTypeYear };
